<template>
  <div class="space-y-6">
    <!-- Connection Status -->
    <div class="bg-gray-stack-1 rounded-token-lg shadow-3d border border-hairline p-6">
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-4">
          <img 
            src="@/assets/integrations/Google-Analytics-logo-01.png" 
            alt="Google Analytics Logo" 
            class="h-12 w-12 bg-white rounded-token-sm p-1 object-contain" 
          />
          <div>
            <h3 class="text-lg font-medium text-gray-900 dark:text-gray-50">Google Analytics</h3>
            <p class="text-sm text-gray-600 dark:text-gray-300">Add your Measurement ID and API secret so Tint Bolt can send visit, lead, and booking events to Google Analytics—outbound tracking, not read access to your GA reports</p>
          </div>
        </div>
        <div class="flex items-center space-x-4">
          <span
            :class="[
              isConnected ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 border border-green-200 dark:border-green-700' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-600',
              'px-3 py-1 rounded-token-sm text-sm font-medium'
            ]"
          >
            {{ isConnected ? 'Connected' : 'Not Connected' }}
          </span>
        </div>
      </div>
    </div>

    <!-- Google Analytics Configuration -->
    <div class="bg-gray-stack-1 rounded-token-lg shadow-3d border border-hairline p-6">
      <form @submit.prevent="onSave">
        <div class="space-y-4">
          <div>
            <label for="measurementId" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Google Analytics Measurement ID
              <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">(Required for event tracking)</span>
            </label>
            <input
              id="measurementId"
              type="text"
              v-model="measurementId"
              autocomplete="off"
              class="block w-full rounded-token-sm border-hairline py-2.5 pl-3 pr-3 text-gray-900 dark:text-gray-50 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-1 focus:ring-inset focus:ring-[var(--theme-primary-500)] focus:border-[var(--theme-primary-500)] sm:text-sm sm:leading-6 font-semibold bg-white dark:bg-gray-stack-2"
              :class="measurementId.length > 5 ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700' : ''"
              placeholder="Enter your Measurement ID (e.g., G-XXXXXXXXXX)"
              pattern="G-[A-Z0-9]+"
            />
            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Your GA4 Measurement ID starts with "G-". Find it in your Google Analytics Admin settings under Data Streams.
            </p>
          </div>

          <!-- Measurement Protocol API Secret -->
          <div>
            <label for="apiSecret" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Measurement Protocol API Secret
              <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">(Required for server-side events)</span>
            </label>
            <div class="relative">
              <input
                id="apiSecret"
                :type="showSecret ? 'text' : 'password'"
                v-model="apiSecret"
                autocomplete="off"
                class="block w-full rounded-token-sm border-hairline py-2.5 pl-3 pr-10 text-gray-900 dark:text-gray-50 ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-1 focus:ring-inset focus:ring-[var(--theme-primary-500)] focus:border-[var(--theme-primary-500)] sm:text-sm sm:leading-6 font-semibold bg-white dark:bg-gray-stack-2 font-mono text-xs"
                :class="apiSecret.length > 10 ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700' : ''"
                placeholder="Enter your Measurement Protocol API Secret"
              />
              <button
                type="button"
                @click="showSecret = !showSecret"
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Show/hide secret"
              >
                <svg v-if="showSecret" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.532 13.532l-3.29-3.29M3 3l18 18" />
                </svg>
                <svg v-else class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
            </div>
            <div class="mt-2 flex items-center justify-between">
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Enables server-side event tracking for better accuracy and privacy compliance.
              </p>
              <button
                type="button"
                @click="showApiSecretHelp = !showApiSecretHelp"
                class="text-sm text-[var(--theme-primary-600)] dark:text-[var(--theme-primary-400)] hover:underline"
              >
                How to get secret
              </button>
            </div>
            
            <!-- Help Section -->
            <div v-if="showApiSecretHelp" class="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-token-sm">
              <h4 class="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">How to get your Measurement Protocol API Secret:</h4>
              <ol class="list-decimal list-inside space-y-1 text-sm text-blue-800 dark:text-blue-300">
                <li>Go to <a href="https://analytics.google.com/" target="_blank" class="underline font-semibold">Google Analytics</a></li>
                <li>Select your GA4 property</li>
                <li>Click on "Admin" (gear icon)</li>
                <li>Under "Data Streams", click on your web stream</li>
                <li>Scroll down to "Measurement Protocol API secrets"</li>
                <li>Click "Create" to generate a new secret</li>
                <li>Copy the secret value and paste it above</li>
              </ol>
              <p class="mt-2 text-xs text-blue-700 dark:text-blue-400">
                <strong>What is the Measurement Protocol?</strong> It's Google's server-side API that sends events directly from your server to Google Analytics. This provides better tracking accuracy, helps with privacy compliance, and ensures events are tracked even when JavaScript is blocked.
              </p>
            </div>
          </div>

          <!-- Save Button -->
          <div class="pt-4 border-t border-hairline">
            <button
              type="submit"
              :disabled="isSaving || !measurementId || measurementId.length < 5"
              class="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-gray-stack-1 dark:bg-gray-stack-2 border border-hairline rounded-token-sm shadow-3d hover:bg-gray-stack-2 dark:hover:bg-gray-stack-3 focus:ring-[color:var(--ring-primary)] ring-2 ring-offset-2 transition-fast disabled:opacity-50 disabled:cursor-not-allowed"
              :style="{ backgroundColor: isSaving ? 'var(--theme-primary-400)' : 'var(--theme-primary-500)' }"
            >
              <svg
                v-if="isSaving"
                class="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647zM20 12c0-3.042-1.135-5.824-3-7.938l-3 2.647A7.962 7.962 0 0120 12h4c0-6.627-5.373-12-12-12v4c3.042 0 5.824 1.135 7.938 3l-2.647 3zM9.171 4.757l-2.828 2.828 1.415 1.414 2.828-2.828-1.415-1.414zM14.828 19.243l2.828-2.828-1.414-1.414-2.828 2.828 1.414 1.414z"
                ></path>
              </svg>
              <CheckCircleIcon
                v-else-if="saveSuccess"
                class="h-5 w-5 text-green-500 mr-2"
                aria-hidden="true"
              />
              <span v-if="isSaving">Saving Configuration...</span>
              <span v-else-if="saveSuccess">Configuration Saved!</span>
              <span v-else>Save Configuration</span>
            </button>
            
            <!-- Success Message -->
            <div v-if="saveSuccess" class="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-token-sm">
              <p class="text-sm text-green-800 dark:text-green-300">
                ✓ Google Analytics configuration saved successfully. Your Measurement ID and API Secret have been updated.
              </p>
            </div>
          </div>

          <!-- Last Updated Timestamp -->
          <div v-if="lastUpdated" class="pt-3 border-t border-hairline">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              Last updated: {{ formatDate(lastUpdated) }}
            </p>
          </div>
        </div>
      </form>
    </div>

    <!-- Events Information Section -->
    <div class="bg-gray-stack-1 rounded-token-lg shadow-3d border border-hairline p-6">
      <div class="flex items-center justify-between mb-4">
        <h4 class="text-lg font-medium text-gray-900 dark:text-gray-50">
          Events You'll Receive from Tint Bolt
        </h4>
        <button
          type="button"
          @click="showEventsInfo = !showEventsInfo"
          class="text-sm text-[var(--theme-primary-600)] dark:text-[var(--theme-primary-400)] hover:underline"
        >
          {{ showEventsInfo ? 'Hide' : 'Show' }} Details
        </button>
      </div>
      
      <div v-if="showEventsInfo" class="space-y-4">
        <p class="text-sm text-gray-600 dark:text-gray-300">
          Once configured, Tint Bolt will automatically send server-side events to your Google Analytics property. Here's what you can expect:
        </p>

        <!-- Key Events -->
        <div class="space-y-3">
          <div class="border-l-4 border-[var(--theme-primary-500)] pl-4 py-2 bg-gray-stack-2 dark:bg-gray-stack-3 rounded-token-sm">
            <h5 class="font-semibold text-gray-900 dark:text-gray-50 mb-1">Core Events</h5>
            <ul class="text-sm text-gray-600 dark:text-gray-300 space-y-1 list-disc list-inside">
              <li><strong>lead</strong> - When a customer requests a quote (includes customer info, vehicle details, service selection, and quote value)</li>
              <li><strong>booking</strong> - When a customer submits or confirms a booking appointment (includes appointment details, service info, and pricing)</li>
              <li><strong>booking_error</strong> - When a booking fails to process (includes error details)</li>
              <li><strong>sms_rejected</strong> - When a customer opts out of SMS notifications</li>
            </ul>
          </div>

          <div class="border-l-4 border-green-500 pl-4 py-2 bg-gray-stack-2 dark:bg-gray-stack-3 rounded-token-sm">
            <h5 class="font-semibold text-gray-900 dark:text-gray-50 mb-1">Interaction Events</h5>
            <ul class="text-sm text-gray-600 dark:text-gray-300 space-y-1 list-disc list-inside">
              <li><strong>vehicle_manufacturer_selected</strong> - Customer selects vehicle manufacturer</li>
              <li><strong>vehicle_model_selected</strong> - Customer selects vehicle model</li>
              <li><strong>vehicle_type_selected</strong> - Customer selects vehicle type</li>
              <li><strong>service_type_selected</strong> - Customer selects a service type</li>
              <li><strong>film_quality_selected</strong> - Customer selects film quality tier</li>
              <li><strong>shades_selected</strong> - Customer selects tint shades</li>
              <li><strong>date_selected</strong> - Customer selects appointment date</li>
              <li><strong>time_slot_selected</strong> - Customer selects appointment time</li>
              <li><strong>quote_generated</strong> - Quote is generated and displayed</li>
            </ul>
          </div>
        </div>

        <!-- Event Parameters -->
        <div class="pt-4 border-t border-hairline">
          <h5 class="font-semibold text-gray-900 dark:text-gray-50 mb-2">Event Parameters</h5>
          <p class="text-sm text-gray-600 dark:text-gray-300 mb-2">
            Each event includes relevant parameters such as:
          </p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <p class="font-medium text-gray-700 dark:text-gray-300 mb-1">Customer Information:</p>
              <ul class="text-gray-600 dark:text-gray-400 list-disc list-inside space-y-0.5">
                <li>customer_name</li>
                <li>customer_phone</li>
                <li>customer_email</li>
                <li>referral</li>
              </ul>
            </div>
            <div>
              <p class="font-medium text-gray-700 dark:text-gray-300 mb-1">Vehicle Information:</p>
              <ul class="text-gray-600 dark:text-gray-400 list-disc list-inside space-y-0.5">
                <li>vehicle_manufacturer</li>
                <li>vehicle_model</li>
                <li>vehicle_type</li>
              </ul>
            </div>
            <div>
              <p class="font-medium text-gray-700 dark:text-gray-300 mb-1">Service Information:</p>
              <ul class="text-gray-600 dark:text-gray-400 list-disc list-inside space-y-0.5">
                <li>service_type</li>
                <li>service_packages</li>
                <li>film_quality</li>
                <li>duration</li>
              </ul>
            </div>
            <div>
              <p class="font-medium text-gray-700 dark:text-gray-300 mb-1">Monetary:</p>
              <ul class="text-gray-600 dark:text-gray-400 list-disc list-inside space-y-0.5">
                <li>value (quote/booking price)</li>
                <li>currency (typically USD)</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- How to View Events -->
        <div class="pt-4 border-t border-hairline">
          <h5 class="font-semibold text-gray-900 dark:text-gray-50 mb-2">How to View Events in Google Analytics</h5>
          <ol class="text-sm text-gray-600 dark:text-gray-300 space-y-1 list-decimal list-inside">
            <li>Go to your Google Analytics property</li>
            <li>Navigate to <strong>Reports</strong> → <strong>Engagement</strong> → <strong>Events</strong></li>
            <li>View real-time events in <strong>Reports</strong> → <strong>Realtime</strong> → <strong>Event count by Event name</strong></li>
            <li>Create custom reports and audiences based on these events</li>
          </ol>
        </div>

        <!-- Note -->
        <div class="pt-4 border-t border-hairline bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-token-sm p-3">
          <p class="text-xs text-blue-800 dark:text-blue-300">
            <strong>Note:</strong> Events are sent server-side via Google's Measurement Protocol API, ensuring reliable tracking even when JavaScript is blocked or when users interact with your landing page in embedded iframes.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { CheckCircleIcon } from "@heroicons/vue/outline";
import { projectFirestore } from "../../../firebase/config";
import { doc, getDoc, updateDoc, onSnapshot } from "firebase/firestore";
import store from "../../../store";

const isSaving = ref(false);
const saveSuccess = ref(false);
const showSecret = ref(false);
const showApiSecretHelp = ref(false);
const showEventsInfo = ref(false);
const lastUpdated = ref(null);
const measurementId = ref("");
const apiSecret = ref("");

const isConnected = computed(() => {
  return !!(measurementId.value && measurementId.value.length > 5 && apiSecret.value && apiSecret.value.length > 10);
});

// Get user document reference
const getUserRef = () => {
  if (!store.state.currentUser?.uid) {
    throw new Error("User UID not found");
  }
  return doc(projectFirestore, "users", store.state.currentUser.uid);
};

// Load connection status and configuration
const loadConnectionStatus = async () => {
  try {
    const userRef = getUserRef();
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const googleAnalytics = userData.config?.googleAnalytics;
      
      // Load Measurement ID and API Secret
      measurementId.value = googleAnalytics?.measurementId || "";
      apiSecret.value = googleAnalytics?.apiSecret || "";
      lastUpdated.value = googleAnalytics?.updatedAt?.toDate?.() || 
                          (googleAnalytics?.updatedAt ? new Date(googleAnalytics.updatedAt) : null);
      
      // Sync Measurement ID to company profile if it exists but hasn't been synced yet
      if (measurementId.value && measurementId.value.length > 5) {
        // Get company profile name(s) - can be single string (profile) or array (companyProfileName)
        let companyProfileNames = [];
        if (userData.profile) {
          companyProfileNames = [userData.profile];
        } else if (userData.companyProfileName) {
          companyProfileNames = Array.isArray(userData.companyProfileName) 
            ? userData.companyProfileName 
            : [userData.companyProfileName];
        } else if (store.state.companyProfileName) {
          companyProfileNames = [store.state.companyProfileName];
        }
        
        if (companyProfileNames.length > 0) {
          const syncPromises = companyProfileNames.map(async (profileName) => {
            try {
              const companyRef = doc(projectFirestore, "companyProfile", profileName);
              const companySnap = await getDoc(companyRef);
              if (companySnap.exists()) {
                const companyData = companySnap.data();
                const existingMeasurementId = companyData.config?.googleAnalyticsMeasurementId;
                
                // Sync if Measurement ID doesn't exist in company profile or is different
                if (!existingMeasurementId || existingMeasurementId !== measurementId.value) {
                  await updateDoc(companyRef, {
                    "config.googleAnalyticsMeasurementId": measurementId.value.trim(),
                    "config.googleAnalyticsUpdatedAt": new Date()
                  });
                  console.log(`Synced Measurement ID to company profile ${profileName}`);
                }
              }
            } catch (error) {
              console.warn(`Failed to sync Measurement ID to company profile ${profileName}:`, error);
            }
          });
          
          await Promise.all(syncPromises);
        }
      }
    }
  } catch (error) {
    console.error("Error loading Google Analytics connection status:", error);
  }
};

// Set up real-time listener
onMounted(async () => {
  await loadConnectionStatus();
  
  // Set up real-time listener for connection status
  if (store.state.currentUser?.uid) {
    const userRef = getUserRef();
    
    onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const userData = docSnap.data();
        const googleAnalytics = userData.config?.googleAnalytics;
        
        // Update Measurement ID and API Secret
        measurementId.value = googleAnalytics?.measurementId || "";
        apiSecret.value = googleAnalytics?.apiSecret || "";
        lastUpdated.value = googleAnalytics?.updatedAt?.toDate?.() || 
                            (googleAnalytics?.updatedAt ? new Date(googleAnalytics.updatedAt) : null);
      } else {
        measurementId.value = "";
        apiSecret.value = "";
        lastUpdated.value = null;
      }
    });
  }
});

// OAuth functions kept for future use but not exposed in UI
// These can be repurposed for Google Calendar integration

// Save Measurement ID and API Secret
const onSave = async () => {
  if (!measurementId.value || measurementId.value.length < 5) {
    return;
  }

  isSaving.value = true;
  saveSuccess.value = false;
  
  try {
    const userRef = getUserRef();
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error("User document not found");
    }
    
    const userData = userSnap.data();
    
    // Get company profile name(s) - can be single string (profile) or array (companyProfileName)
    let companyProfileNames = [];
    if (userData.profile) {
      // Single profile stored as string
      companyProfileNames = [userData.profile];
    } else if (userData.companyProfileName) {
      // Array of profiles
      companyProfileNames = Array.isArray(userData.companyProfileName) 
        ? userData.companyProfileName 
        : [userData.companyProfileName];
    } else if (store.state.companyProfileName) {
      // Fallback to store state
      companyProfileNames = [store.state.companyProfileName];
    }
    
    // Save to user document
    await updateDoc(userRef, {
      "config.googleAnalytics.measurementId": measurementId.value.trim(),
      "config.googleAnalytics.apiSecret": apiSecret.value.trim(),
      "config.googleAnalytics.connected": true,
      "config.googleAnalytics.updatedAt": new Date()
    });
    
    // Also save Measurement ID to company profile(s) so landing page can access it
    if (companyProfileNames.length > 0) {
      const updatePromises = companyProfileNames.map(async (profileName) => {
        try {
          const companyRef = doc(projectFirestore, "companyProfile", profileName);
          await updateDoc(companyRef, {
            "config.googleAnalyticsMeasurementId": measurementId.value.trim(),
            "config.googleAnalyticsUpdatedAt": new Date()
          });
          console.log(`Successfully updated company profile ${profileName} with Measurement ID`);
        } catch (error) {
          console.error(`Failed to update company profile ${profileName}:`, error);
        }
      });
      
      await Promise.all(updatePromises);
    } else {
      console.warn("No company profile found to update. Measurement ID saved to user document only.");
    }
    
    lastUpdated.value = new Date();
    saveSuccess.value = true;
    
    // Hide success message after 3 seconds
    setTimeout(() => {
      saveSuccess.value = false;
    }, 3000);
  } catch (error) {
    console.error("Error saving Google Analytics configuration:", error);
    alert("Failed to save Google Analytics configuration. Please try again.");
  } finally {
    isSaving.value = false;
  }
};

// Format date for display
const formatDate = (date) => {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString();
};

// Expose for parent component
defineExpose({
  isConnected,
  refreshData: loadConnectionStatus
});
</script>

